import { DateTime } from 'luxon';
import * as hyejoo from 'smg/admin/hyejoo';
import Batch from 'smg/batch';
import * as models from 'smg/models';

const hyejooDateFormat = 'yyyy-MM-dd';
export const CacheKey: string = 'yyyyMM';
const cachedOrders = new Map<string, Promise<hyejoo.Order[]>>();
let cachedDetails: Promise<Map<string, models.Detail>>;
let cachedCompanies: Promise<Map<string, hyejoo.Realm>>;
let cachedArtists: Promise<Map<string, hyejoo.ArtistReward[]>>;

export function Orders(date: DateTime, breakCache: boolean, b: Batch): Promise<Map<string, Promise<hyejoo.Order[]>>> {
    // Callers get a snapshot: the month they asked for stays in it even if a
    // failed load is forgotten from the shared cache before they read it.
    if (cachedOrders.has(date.toFormat(CacheKey)) && !breakCache) {
        return Promise.resolve(new Map(cachedOrders));
    }

    const orders = b.get<hyejoo.Orders>('/orders', {
        params: {
            q: `pendingUpdated:"${date.startOf('month').toFormat(hyejooDateFormat)} to ${date.endOf('month').toFormat(hyejooDateFormat)}" active:yes pending:no preorder:no`,
            max: 10000, // if numbers ever get weird here is why, suffering from success
            p: 0,
            data: 'totalSansTax,companyID,pendingUpdated,originalSalesTeamID',
        },
    });

    const promiseOrders = orders.then(o => o.data.orders);

    cachedOrders.set(date.toFormat(CacheKey), promiseOrders);
    // A failed load is not a month's orders: forget it so the next request
    // for that month tries again instead of replaying the rejection.
    promiseOrders.catch(() => {
        if (cachedOrders.get(date.toFormat(CacheKey)) === promiseOrders) {
            cachedOrders.delete(date.toFormat(CacheKey));
        }
    });

    return Promise.resolve(new Map(cachedOrders));
}

export function Details(b: Batch): Promise<Map<string, models.Detail>> {
    if (cachedDetails !== undefined) {
        return cachedDetails;
    }

    cachedDetails = b.get<hyejoo.Details>('/details', {
        params: {
            q: 'id:goalAmount,quotaAmount,defaultGoalPercentage',
            data: 'json',
        },
    }).then(details => {
        const m = new Map<string, models.Detail>();
        for (const d of details.data.details) {
            if (d.id) {
                m.set(d.id, d);
            }
        }
        return m;
    });

    return cachedDetails;
}

export function Realms(b: Batch): Promise<Map<string, hyejoo.Realm>> {
    if (cachedCompanies !== undefined) {
        return cachedCompanies;
    }

    cachedCompanies = b.get<hyejoo.Realms>('/realms', {
        params: {
            data: 'name,iconfa5',
        },
    }).then(realms => {
        const m = new Map<string, hyejoo.Realm>();
        for (const r of realms.data.realms) {
            if (r.id) {
                m.set(r.id, r);
            }
        }
        return m;
    });

    return cachedCompanies;
}

export function ArtistRewards(b: Batch, breakCache: boolean = false): Promise<Map<string, hyejoo.ArtistReward[]>> {
    if (cachedArtists !== undefined && !breakCache) {
        return cachedArtists;
    }

    const now = DateTime.now();

    cachedArtists = b.get<hyejoo.ArtistRewards>('/artist-rewards', {
        params: {
            q: `orderPendingUpdated:"${now.startOf('month').toFormat(hyejooDateFormat)} to ${now.endOf('month').toFormat(hyejooDateFormat)}"`,
            data: 'user,points,userID',
            max: 10000, // if numbers ever get weird here is why, suffering from success
            p: 0,
        },
    }).then(rewards => {
        const m = new Map<string, hyejoo.ArtistReward[]>();
        for (const r of rewards.data.artistRewards) {
            if (r.userID) {
                const rewardList = m.get(r.userID);
                if (rewardList) {
                    rewardList.push(r);
                } else {
                    m.set(r.userID, [r]);
                }
            }
        }
        return m;
    });

    return cachedArtists;
}
